import axios, { AxiosProgressEvent } from 'axios'
import * as tus from 'tus-js-client'
import { getServiceToken, getToken } from '../utils'

const UPLOAD_BASE_URL = `${import.meta.env.VITE_FILE_SERVICE_URL}/message`
const STORAGE_BASE_URL = `${import.meta.env.VITE_FILE_SERVICE_URL}/storage`
const UPLOAD_CONCURRENCY_LIMIT = 3

/**
 * Hard cap on attachments per message. Each file is its own request to the
 * file service, which rate-limits chat uploads per minute, so an unbounded
 * selection floods that limit and the extra uploads come back as
 * "Too many requests".
 */
export const MAX_FILES_PER_MESSAGE = 5

/**
 * Bearer header for the now-authenticated file-services endpoints.
 * (The tus upload itself goes directly to Bunny and is unaffected.)
 */
const authHeaders = (): Record<string, string> => {
  // The file service validates the main-platform JWT, not the chat token.
  const token = getServiceToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * The chat a file is sent to (DB-2367): the same type and id as the message/send
 * payload. The file service checks it with the chat API, using the chat token, and
 * stores the file in that chat's folder (`chats/<chat>/...`).
 */
export interface UploadChat {
  type: 'direct' | 'group' | 'broadcast'
  id: string | number
}

const chatFields = (chat?: UploadChat): Record<string, string> =>
  chat ? { chatType: chat.type, chatId: String(chat.id) } : {}

const chatHeaders = (chat?: UploadChat): Record<string, string> => {
  const chatToken = chat ? getToken() : null
  return chatToken ? { 'X-Chat-Token': chatToken } : {}
}

interface UploadResponse {
  message: string
  data: Array<{ url: string; thumbnailUrl?: string }>
}

interface BunnyUploadSessionResponse {
  message?: string
  data?: {
    videoId: string
    libraryId: string
    expiresAt: number
    signature: string
    tusEndpoint: string
    url: string
    thumbnailUrl: string
  }
}

interface BunnyVideoStatusResponse {
  message?: string
  data?: {
    videoId: string
    status: number
    encodeProgress: number | null
    url: string
    thumbnailUrl: string
  }
}

interface FileUploadOptions {
  onProgress?: (fileIndex: number, progress: number) => void
  chat?: UploadChat
}

export interface FileUploadResult {
  url: string
  thumbnailUrl?: string
  type: 'image' | 'video' | 'document'
}

// Also used to pick icons for already-sent attachments, so 'svg' stays here;
// SVG uploads are blocked by isSvgFile below.
export const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'heic',
  'heif',
  'avif',
  'bmp',
  'svg',
  'tiff',
  'tif',
  'jfif',
  'ico',
]
export const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'ogv']

const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || ''
}

// SVG uploads are not supported anywhere (the upload APIs reject them too)
export const isSvgFile = (file: File): boolean => {
  const ext = getFileExtension(file.name)
  return file.type === 'image/svg+xml' || ext === 'svg' || ext === 'svgz'
}

// Explicit image types for <input accept>: `image/*` would also offer SVG files
export const IMAGE_ACCEPT_TYPES =
  'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/avif,image/bmp,image/tiff,image/x-icon'

export const isImageUploadFile = (file: File): boolean => {
  if (isSvgFile(file)) return false
  const ext = getFileExtension(file.name)
  return file.type.startsWith('image/') || IMAGE_EXTENSIONS.includes(ext)
}

export const isVideoUploadFile = (file: File): boolean => {
  const ext = getFileExtension(file.name)
  return (
    file.type.startsWith('video/') ||
    VIDEO_EXTENSIONS.includes(ext) ||
    (ext === 'ogg' && file.type.startsWith('video/'))
  )
}

const runWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results: R[] = new Array(items.length)
  let nextIndex = 0

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex
      nextIndex += 1
      results[currentIndex] = await task(items[currentIndex], currentIndex)
    }
  })

  await Promise.all(workers)

  return results
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const waitForBunnyReady = async (
  videoId: string,
  maxAttempts = 5,
  intervalMs = 6000,
): Promise<{ url: string; thumbnailUrl: string }> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const response = await fetch(`${STORAGE_BASE_URL}/video-status?videoId=${encodeURIComponent(videoId)}`, {
      method: 'GET',
      headers: { ...authHeaders() },
    })

    const json = (await response.json()) as BunnyVideoStatusResponse
    const payload = json?.data

    if (!response.ok || !payload) {
      throw new Error(json?.message || 'Failed to check Bunny video status')
    }

    if (payload.status > 0 || attempt === maxAttempts - 1) {
      return {
        url: payload.url,
        thumbnailUrl: payload.thumbnailUrl,
      }
    }

    await sleep(intervalMs)
  }

  throw new Error('Video processing timed out. Bunny status did not become ready.')
}

/**
 * Coarse readiness of a Bunny Stream video, from the file service's
 * authoritative status endpoint. Bunny status codes: 0 created, 1 uploaded,
 * 2 processing, 3 transcoding, 4 finished, 5 error, 6 upload failed,
 * 7/8 just-in-time streaming states (playable).
 * Returns null on transient errors so callers can keep waiting.
 */
export type BunnyVideoReadiness = 'ready' | 'processing' | 'failed'

export const fetchBunnyVideoReadiness = async (videoId: string): Promise<BunnyVideoReadiness | null> => {
  try {
    const response = await fetch(`${STORAGE_BASE_URL}/video-status?videoId=${encodeURIComponent(videoId)}`, {
      method: 'GET',
      headers: { ...authHeaders() },
    })
    if (!response.ok) return null

    const json = (await response.json()) as BunnyVideoStatusResponse
    const status = json?.data?.status
    if (typeof status !== 'number') return null

    if (status === 5 || status === 6) return 'failed'
    if (status === 4 || status >= 7) return 'ready'
    return 'processing'
  } catch {
    return null
  }
}

const uploadSingleVideoWithTus = async (
  file: File,
  onProgress?: (progress: number) => void,
  chat?: UploadChat,
): Promise<{ url: string; thumbnailUrl: string }> => {
  const sessionResponse = await fetch(`${STORAGE_BASE_URL}/generate-video-id`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...chatHeaders(chat),
    },
    body: JSON.stringify({ title: file.name, ...chatFields(chat) }),
  })

  const sessionJson = (await sessionResponse.json()) as BunnyUploadSessionResponse
  const session = sessionJson?.data

  if (!sessionResponse.ok || !session) {
    throw new Error(sessionJson?.message || 'Failed to initialize Bunny Stream upload session')
  }

  await new Promise<void>((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: session.tusEndpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      uploadSize: file.size,
      removeFingerprintOnSuccess: true,
      storeFingerprintForResuming: false,
      headers: {
        AuthorizationSignature: session.signature,
        AuthorizationExpire: String(session.expiresAt),
        VideoId: session.videoId,
        LibraryId: String(session.libraryId),
      },
      metadata: {
        filename: file.name,
        filetype: file.type || 'video/mp4',
      },
      onError: (error) => reject(error),
      onProgress: (uploadedBytes, totalBytes) => {
        if (!totalBytes || !onProgress) return
        onProgress(Math.round((uploadedBytes / totalBytes) * 100))
      },
      onSuccess: () => {
        onProgress?.(100)
        resolve()
      },
    })

    upload.start()
  })

  return waitForBunnyReady(session.videoId)
}

/**
 * Upload documents with progress tracking
 */
export const uploadDocumentsWithProgress = async (
  files: File[],
  options: FileUploadOptions = {},
): Promise<string[]> => {
  return runWithConcurrency(files, UPLOAD_CONCURRENCY_LIMIT, async (file, index) => {
    const formData = new FormData()
    Object.entries(chatFields(options.chat)).forEach(([key, value]) => formData.append(key, value))
    formData.append('document', file)

    const { data } = await axios.post<UploadResponse>(`${UPLOAD_BASE_URL}/documents`, formData, {
      headers: { ...authHeaders(), ...chatHeaders(options.chat) },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (!progressEvent.total) return

        const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        options.onProgress?.(index, percentComplete)
      },
    })

    const uploadedUrl = data.data[0]?.url
    if (!uploadedUrl) {
      throw new Error('Document upload did not return a URL')
    }

    return uploadedUrl
  })
}

/**
 * Upload images with progress tracking
 */
export const uploadImagesWithProgress = async (files: File[], options: FileUploadOptions = {}): Promise<string[]> => {
  return runWithConcurrency(files, UPLOAD_CONCURRENCY_LIMIT, async (file, index) => {
    const formData = new FormData()
    Object.entries(chatFields(options.chat)).forEach(([key, value]) => formData.append(key, value))
    formData.append('image', file)

    const { data } = await axios.post<UploadResponse>(`${UPLOAD_BASE_URL}/images`, formData, {
      headers: { ...authHeaders(), ...chatHeaders(options.chat) },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (!progressEvent.total) return

        const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        options.onProgress?.(index, percentComplete)
      },
    })

    const uploadedUrl = data.data[0]?.url
    if (!uploadedUrl) {
      throw new Error('Image upload did not return a URL')
    }

    return uploadedUrl
  })
}

/**
 * Upload videos with progress tracking
 */
export const uploadVideosWithProgress = async (
  files: File[],
  options: FileUploadOptions = {},
): Promise<Array<{ url: string; thumbnailUrl: string }>> => {
  const results: Array<{ url: string; thumbnailUrl: string }> = []

  for (const [index, file] of files.entries()) {
    const uploaded = await uploadSingleVideoWithTus(
      file,
      (progress) => {
        options.onProgress?.(index, progress)
      },
      options.chat,
    )

    results.push({
      url: uploaded.url,
      thumbnailUrl: uploaded.thumbnailUrl || '',
    })
  }

  return results
}

const categorizeFiles = (files: File[]): { images: File[]; videos: File[]; documents: File[] } => {
  const images: File[] = []
  const videos: File[] = []
  const documents: File[] = []

  files.forEach((file) => {
    if (isImageUploadFile(file)) {
      images.push(file)
    } else if (isVideoUploadFile(file)) {
      videos.push(file)
    } else {
      documents.push(file)
    }
  })

  return { images, videos, documents }
}

/**
 * Upload files with progress tracking and return categorized results
 * Maintains original file order
 */
export const uploadFilesAndGetUrlsWithProgress = async (
  files: File[],
  onProgress?: (fileIndex: number, progress: number) => void,
  chat?: UploadChat,
): Promise<FileUploadResult[]> => {
  const { images, videos, documents } = categorizeFiles(files)
  const results: FileUploadResult[] = []

  // Create a map to track progress for each original file
  const fileProgressMap = new Map<File, { categoryIndex: number; categoryName: string }>(
    [
      ...images.map((f, i) => [f, { categoryIndex: i, categoryName: 'image' }] as const),
      ...videos.map((f, i) => [f, { categoryIndex: i, categoryName: 'video' }] as const),
      ...documents.map((f, i) => [f, { categoryIndex: i, categoryName: 'document' }] as const),
    ].map(([f, info]) => [f, info]),
  )

  // Track total files and results
  let uploadedCount = 0
  const resultMap = new Map<File, FileUploadResult>()

  // Wrapper function to handle progress for each category
  const handleProgressWrapper =
    (_categoryName: string, categoryFiles: File[]) => (fileIndex: number, progress: number) => {
      // Find which original file index this corresponds to
      const file = categoryFiles[fileIndex]
      if (file && onProgress) {
        const originalIndex = Array.from(fileProgressMap.keys()).indexOf(file)
        onProgress(originalIndex, progress)
      }
    }

  try {
    if (images.length > 0) {
      const urls = await uploadImagesWithProgress(images, {
        onProgress: handleProgressWrapper('image', images),
        chat,
      })
      urls.forEach((url, i) => {
        resultMap.set(images[i], { url, type: 'image' })
        uploadedCount++
      })
    }

    if (videos.length > 0) {
      const videoData = await uploadVideosWithProgress(videos, {
        onProgress: handleProgressWrapper('video', videos),
        chat,
      })
      videoData.forEach((data, i) => {
        resultMap.set(videos[i], {
          url: data.url,
          thumbnailUrl: data.thumbnailUrl,
          type: 'video',
        })
        uploadedCount++
      })
    }

    if (documents.length > 0) {
      const urls = await uploadDocumentsWithProgress(documents, {
        onProgress: handleProgressWrapper('document', documents),
        chat,
      })
      urls.forEach((url, i) => {
        resultMap.set(documents[i], { url, type: 'document' })
        uploadedCount++
      })
    }

    // Reconstruct results in original file order
    files.forEach((file) => {
      const result = resultMap.get(file)
      if (result) {
        results.push(result)
      }
    })

    return results
  } catch (error) {
    console.error('Error uploading files:', error)
    throw error
  }
}
